/**
 * +world-resources: every breakable / cache / node of a dungeon merged into a few static meshes,
 * one per CHUNK_YD square of the map (plus its outline hull and shadow depth pass), so the
 * frustum culls the far ones (one dungeon-wide mesh was ~90k triangles drawn three times a frame). Per-object animation lives in a tiny float
 * state texture read in the vertex shader, so a hit, a wobble or a break is a few texel writes,
 * never a rebuild or an extra draw call.
 *
 * Vertex attributes: aObj (object index), aPart (0 intact, 1 remains, 2 hint), aPivot (base
 * centre in world space). State texture (N x 2 RGBA float):
 *   row 0: intact scale, hit flash 0..1, tilt x, tilt z (shear per yd of height)
 *   row 1: remains scale, hint scale, near highlight 0..1, intact rise (yd)
 */
import * as THREE from 'three';
import type { WorldObjectDef } from '@keystone/sim';
import { ATLAS_WHITE } from '../assets';
import { objY } from '../ground';
import { type HullRegistry, U, addOutlineNormals, ownedToonMaterial } from '../style';
import { NEAR_RIM } from '../fx/loot/palette';
import { type ModelLibrary, type ModelPart, PART_INDEX } from './models';

const STATE_GLSL_V = `
attribute float aObj; attribute float aPart; attribute vec3 aPivot;
uniform sampler2D uObjState;`;

/** Displaces `transformed` (world space: the mesh has an identity transform). */
const DISPLACE_GLSL = `
{
  int oi = int(aObj + 0.5);
  vec4 s0 = texelFetch(uObjState, ivec2(oi, 0), 0);
  vec4 s1 = texelFetch(uObjState, ivec2(oi, 1), 0);
  float isIntact = aPart < 0.5 ? 1.0 : 0.0;
  float sc = aPart < 0.5 ? s0.x : (aPart < 1.5 ? s1.x : s1.y);
  vec3 l = transformed - aPivot;
  float fl = s0.y * isIntact;
  l.y *= 1.0 - 0.16 * fl;
  l.xz *= 1.0 + 0.09 * fl;
  l.xz += l.y * s0.zw * isIntact;
  l *= sc;
  transformed = aPivot + l + vec3(0.0, s1.w * isIntact, 0.0);
  wlFlash = fl;
  wlNear = s1.z * isIntact;
}`;

export interface ObjectState {
  intact: number;
  flash: number;
  tiltX: number;
  tiltZ: number;
  remains: number;
  hint: number;
  near: number;
  rise: number;
}

/** Side of the square map cells the objects are merged by (yards). */
const CHUNK_YD = 40;

export class WorldObjectsMesh {
  readonly group = new THREE.Group();
  readonly count: number;
  private readonly data: Float32Array;
  private readonly tex: THREE.DataTexture;
  private readonly geos: THREE.BufferGeometry[] = [];
  private readonly mat: THREE.MeshToonMaterial;
  private readonly hullMat: THREE.MeshBasicMaterial;
  private readonly depthMat: THREE.MeshDepthMaterial;
  private readonly hullMeshes: THREE.Mesh[] = [];
  private readonly hulls: HullRegistry | null;
  private dirty = true;

  constructor(defs: readonly WorldObjectDef[], lib: ModelLibrary, atlas: THREE.Texture | null, hulls: HullRegistry | null) {
    this.count = defs.length;
    this.hulls = hulls;
    const w = Math.max(1, defs.length);
    this.data = new Float32Array(w * 2 * 4);
    this.tex = new THREE.DataTexture(this.data, w, 2, THREE.RGBAFormat, THREE.FloatType);
    this.tex.minFilter = this.tex.magFilter = THREE.NearestFilter;
    this.tex.generateMipmaps = false;
    this.tex.needsUpdate = true;
    const uState = { value: this.tex };
    const uNear = { value: new THREE.Vector3(...NEAR_RIM) };

    this.mat = ownedToonMaterial({ color: 0xffffff, map: atlas, vertexColors: true });
    this.mat.name = 'worldObjects';
    const toon = this.mat.onBeforeCompile.bind(this.mat);
    this.mat.onBeforeCompile = (s, r) => {
      toon(s, r);
      s.uniforms['uObjState'] = uState;
      s.uniforms['uNearColor'] = uNear;
      s.vertexShader = s.vertexShader
        .replace('#include <common>', `#include <common>${STATE_GLSL_V}\nvarying float wlFlash; varying float wlNear;`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>${DISPLACE_GLSL}`);
      s.fragmentShader = s.fragmentShader.replace('#include <common>', '#include <common>\nvarying float wlFlash; varying float wlNear; uniform vec3 uNearColor;').replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        {
          float fr = 1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0);
          totalEmissiveRadiance += vec3(1.0, 0.9, 0.72) * wlFlash * 0.85;
          totalEmissiveRadiance += uNearColor * wlNear * (0.05 + smoothstep(0.5, 0.85, fr) * 0.5);
        }`,
      );
    };
    this.mat.customProgramCacheKey = () => 'worldObjects';

    // Outline hull: same displacement, then the inverted-hull push along smoothed normals.
    this.hullMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0.11, 0.08, 0.12), side: THREE.BackSide });
    this.hullMat.onBeforeCompile = (s) => {
      s.uniforms['uObjState'] = uState;
      s.uniforms['uOutline'] = U.uOutline;
      s.vertexShader = s.vertexShader
        .replace('#include <common>', `#include <common>${STATE_GLSL_V}\nattribute vec3 outlineNormal; uniform float uOutline; float wlFlash; float wlNear;`)
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>${DISPLACE_GLSL}
          {
            vec4 oMV = modelViewMatrix * vec4(transformed, 1.0);
            float live = step(0.02, length(transformed - aPivot));
            transformed += normalize(outlineNormal) * uOutline * clamp(-oMV.z, 5.0, 60.0) * 0.0021 * live;
          }`,
        );
    };
    this.hullMat.customProgramCacheKey = () => 'worldObjectsHull';

    this.depthMat = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
    this.depthMat.onBeforeCompile = (s) => {
      s.uniforms['uObjState'] = uState;
      s.vertexShader = s.vertexShader
        .replace('#include <common>', `#include <common>${STATE_GLSL_V}\nfloat wlFlash; float wlNear;`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>${DISPLACE_GLSL}`);
    };
    this.depthMat.customProgramCacheKey = () => 'worldObjectsDepth';

    // Merge per map cell (object indices stay global: the state texture is shared).
    const cells = new Map<string, number[]>();
    defs.forEach((d, i) => {
      const k = `${Math.floor(d.pos.x / CHUNK_YD)},${Math.floor(d.pos.y / CHUNK_YD)}`;
      let c = cells.get(k);
      if (!c) cells.set(k, (c = []));
      c.push(i);
    });
    for (const only of cells.values()) {
      const geo = buildGeometry(defs, lib, only);
      // Displacement (rise, wobble) stays near the pivots; pad the culling sphere for it.
      if (geo.boundingSphere) geo.boundingSphere.radius += 2;
      this.geos.push(geo);
      const body = new THREE.Mesh(geo, this.mat);
      body.name = 'worldObjects';
      body.castShadow = true;
      body.receiveShadow = true;
      body.customDepthMaterial = this.depthMat;
      body.userData['noStyle'] = true;
      const hull = new THREE.Mesh(geo, this.hullMat);
      hull.name = 'worldObjects:hull';
      hull.userData['noStyle'] = true;
      hull.userData['hull'] = true;
      hulls?.prop.add(hull);
      this.hullMeshes.push(hull);
      this.group.add(body, hull);
    }
    this.group.name = 'worldObjects';

    for (let i = 0; i < defs.length; i++) {
      const hidden = !!defs[i]!.hidden;
      this.set(i, { intact: hidden ? 0 : 1, flash: 0, tiltX: 0, tiltZ: 0, remains: 0, hint: hidden ? 1 : 0, near: 0, rise: 0 });
    }
  }

  /** Write one object's state (cheap; uploads once per frame in flush()). */
  set(i: number, s: ObjectState): void {
    const d = this.data;
    const o0 = i * 4;
    const o1 = (this.count + i) * 4;
    if (
      d[o0] === s.intact && d[o0 + 1] === s.flash && d[o0 + 2] === s.tiltX && d[o0 + 3] === s.tiltZ &&
      d[o1] === s.remains && d[o1 + 1] === s.hint && d[o1 + 2] === s.near && d[o1 + 3] === s.rise
    )
      return;
    d[o0] = s.intact;
    d[o0 + 1] = s.flash;
    d[o0 + 2] = s.tiltX;
    d[o0 + 3] = s.tiltZ;
    d[o1] = s.remains;
    d[o1 + 1] = s.hint;
    d[o1 + 2] = s.near;
    d[o1 + 3] = s.rise;
    this.dirty = true;
  }

  flush(): void {
    if (!this.dirty) return;
    this.dirty = false;
    this.tex.needsUpdate = true;
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const h of this.hullMeshes) this.hulls?.prop.delete(h);
    for (const g of this.geos) g.dispose();
    this.mat.dispose();
    this.hullMat.dispose();
    this.depthMat.dispose();
    this.tex.dispose();
  }
}

/** Bake the objects' parts (all, or the `only` indices) into one world-space geometry. Exported for tests. */
export function buildGeometry(defs: readonly WorldObjectDef[], lib: ModelLibrary, only?: readonly number[]): THREE.BufferGeometry {
  type Item = { part: ModelPart; obj: number; set: number; m: THREE.Matrix4; pivot: THREE.Vector3 };
  const items: Item[] = [];
  let total = 0;
  const Qy = new THREE.Quaternion();
  const AY = new THREE.Vector3(0, 1, 0);
  for (const i of only ?? defs.keys()) {
    const d = defs[i]!;
    const model = lib.model(d.kind);
    const v = d.variant ?? 0;
    const scale = 0.92 + 0.16 * v;
    const yaw = (d.facing ?? 0) + (v - 0.5) * 1.2;
    const pivot = new THREE.Vector3(d.pos.x, objY(d), -d.pos.y);
    const place = new THREE.Matrix4().compose(pivot, Qy.setFromAxisAngle(AY, yaw), new THREE.Vector3(scale, scale, scale));
    for (const set of ['intact', 'remains', 'hint'] as const) {
      for (const part of model[set]) {
        items.push({ part, obj: i, set: PART_INDEX[set], m: new THREE.Matrix4().multiplyMatrices(place, part.matrix), pivot });
        total += (part.geo.attributes['position'] as THREE.BufferAttribute).count;
      }
    }
  }
  const pos = new Float32Array(total * 3);
  const nrm = new Float32Array(total * 3);
  const uvs = new Float32Array(total * 2);
  const col = new Float32Array(total * 3);
  const aObj = new Float32Array(total);
  const aPart = new Float32Array(total);
  const aPivot = new Float32Array(total * 3);
  const N3 = new THREE.Matrix3();
  let o = 0;
  for (const it of items) {
    const g = it.part.geo;
    const P = g.attributes['position'] as THREE.BufferAttribute;
    const Nn = g.attributes['normal'] as THREE.BufferAttribute | undefined;
    const Uv = g.attributes['uv'] as THREE.BufferAttribute | undefined;
    const C = g.attributes['color'] as THREE.BufferAttribute | undefined;
    const e = it.m.elements;
    N3.getNormalMatrix(it.m);
    const ne = N3.elements;
    const [tr, tg, tb] = it.part.tint;
    const idx = g.index;
    const count = idx ? idx.count : P.count;
    for (let k = 0; k < count; k++, o++) {
      const i = idx ? idx.getX(k) : k;
      const x = P.getX(i);
      const y = P.getY(i);
      const z = P.getZ(i);
      pos[o * 3] = e[0]! * x + e[4]! * y + e[8]! * z + e[12]!;
      pos[o * 3 + 1] = e[1]! * x + e[5]! * y + e[9]! * z + e[13]!;
      pos[o * 3 + 2] = e[2]! * x + e[6]! * y + e[10]! * z + e[14]!;
      const a = Nn ? Nn.getX(i) : 0;
      const b = Nn ? Nn.getY(i) : 1;
      const c = Nn ? Nn.getZ(i) : 0;
      const nx = ne[0]! * a + ne[3]! * b + ne[6]! * c;
      const ny = ne[1]! * a + ne[4]! * b + ne[7]! * c;
      const nz = ne[2]! * a + ne[5]! * b + ne[8]! * c;
      const l = 1 / (Math.hypot(nx, ny, nz) || 1);
      nrm[o * 3] = nx * l;
      nrm[o * 3 + 1] = ny * l;
      nrm[o * 3 + 2] = nz * l;
      uvs[o * 2] = Uv ? Uv.getX(i) : ATLAS_WHITE[0];
      uvs[o * 2 + 1] = Uv ? Uv.getY(i) : ATLAS_WHITE[1];
      col[o * 3] = (C ? C.getX(i) : 1) * tr;
      col[o * 3 + 1] = (C ? C.getY(i) : 1) * tg;
      col[o * 3 + 2] = (C ? C.getZ(i) : 1) * tb;
      aObj[o] = it.obj;
      aPart[o] = it.set;
      aPivot[o * 3] = it.pivot.x;
      aPivot[o * 3 + 1] = it.pivot.y;
      aPivot[o * 3 + 2] = it.pivot.z;
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geo.setAttribute('aObj', new THREE.BufferAttribute(aObj, 1));
  geo.setAttribute('aPart', new THREE.BufferAttribute(aPart, 1));
  geo.setAttribute('aPivot', new THREE.BufferAttribute(aPivot, 3));
  if (total > 0) addOutlineNormals(geo);
  geo.computeBoundingSphere();
  return geo;
}
